import {
  chevronLeftIcon,
  SelectionIcon,
} from "@excalidraw/excalidraw/components/icons";
import { useEffect, useState } from "react";

import { useAtom, useSetAtom } from "../app-jotai";
import { authApi, ApiError } from "../data/api";
import { authPageStateAtom, currentUserAtom } from "../data/authState";
import { workspacePath } from "../data/workspaceRoutes";
import { BrandLogo } from "./BrandLogo";

import "./AuthPage.scss";

type AuthMode = "login" | "signup";

export const AuthPage = () => {
  const [pageState, setPageState] = useAtom(authPageStateAtom);
  const setCurrentUser = useSetAtom(currentUserAtom);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const syncRoute = () => {
      if (window.location.pathname === "/login") {
        setPageState({ isOpen: true, mode: "login" });
      } else if (window.location.pathname === "/signup") {
        setPageState({ isOpen: true, mode: "signup" });
      } else if (window.location.pathname !== "/admin") {
        setPageState({ isOpen: false });
      }
    };
    window.addEventListener("popstate", syncRoute);
    return () => window.removeEventListener("popstate", syncRoute);
  }, [setPageState]);

  useEffect(() => {
    if (!pageState.isOpen) {
      return;
    }
    const page = document.querySelector(".AuthPage");
    const siblings = page?.parentElement
      ? Array.from(page.parentElement.children).filter(
          (element) => element !== page,
        )
      : [];
    const previousState = siblings.map((element) => ({
      element,
      ariaHidden: element.getAttribute("aria-hidden"),
      wasInert: element.hasAttribute("inert"),
    }));
    siblings.forEach((element) => {
      element.setAttribute("aria-hidden", "true");
      element.setAttribute("inert", "");
    });

    return () => {
      previousState.forEach(({ element, ariaHidden, wasInert }) => {
        if (ariaHidden === null) {
          element.removeAttribute("aria-hidden");
        } else {
          element.setAttribute("aria-hidden", ariaHidden);
        }
        if (!wasInert) {
          element.removeAttribute("inert");
        }
      });
    };
  }, [pageState.isOpen]);

  if (!pageState.isOpen) {
    return null;
  }

  const mode = pageState.mode;

  const changeMode = (nextMode: AuthMode) => {
    setError(null);
    setPassword("");
    window.history.pushState({}, document.title, `/${nextMode}`);
    setPageState({ isOpen: true, mode: nextMode });
  };

  const leaveAuth = () => {
    window.history.pushState({}, document.title, "/");
    setPageState({ isOpen: false });
    setError(null);
    setPassword("");
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setIsSubmitting(true);
    try {
      const user =
        mode === "signup"
          ? await authApi.signup(email, password)
          : await authApi.login(email, password);
      window.history.replaceState(
        {},
        document.title,
        user.role === "ADMIN" ? "/admin" : workspacePath(user.publicId),
      );
      setPageState({ isOpen: false });
      setCurrentUser(user);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Something went wrong");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="AuthPage">
      <div className="AuthPage__canvas" aria-hidden="true">
        <div className="AuthPage__toolbar">
          <span className="AuthPage__tool AuthPage__tool--active">
            {SelectionIcon}
          </span>
          <span className="AuthPage__tool AuthPage__tool--square" />
          <span className="AuthPage__tool AuthPage__tool--diamond" />
          <span className="AuthPage__tool AuthPage__tool--circle" />
          <span className="AuthPage__tool AuthPage__tool--line" />
          <span className="AuthPage__tool AuthPage__tool--text">T</span>
        </div>
        <p className="AuthPage__canvasHint">Make space for your next idea.</p>
        <span className="AuthPage__stroke AuthPage__stroke--one" />
        <span className="AuthPage__stroke AuthPage__stroke--two" />
      </div>

      <button type="button" className="AuthPage__back" onClick={leaveAuth}>
        {chevronLeftIcon}
        Back to canvas
      </button>

      <section className="AuthPage__panel" aria-labelledby="auth-heading">
        <BrandLogo className="AuthPage__logo" />

        <div className="AuthPage__modeSwitch" aria-label="Authentication mode">
          <button
            type="button"
            className={mode === "login" ? "is-active" : undefined}
            aria-pressed={mode === "login"}
            onClick={() => changeMode("login")}
          >
            Log in
          </button>
          <button
            type="button"
            className={mode === "signup" ? "is-active" : undefined}
            aria-pressed={mode === "signup"}
            onClick={() => changeMode("signup")}
          >
            Sign up
          </button>
        </div>

        <div className="AuthPage__intro">
          <p>{mode === "signup" ? "New workspace" : "Your workspace"}</p>
          <h1 id="auth-heading">
            {mode === "signup" ? "Create your account" : "Welcome back"}
          </h1>
          <span>
            {mode === "signup"
              ? "Start your first canvas in seconds."
              : "Log in to continue where you left off."}
          </span>
        </div>

        <form className="AuthPage__form" onSubmit={handleSubmit}>
          <label htmlFor="auth-email">
            Email
            <input
              id="auth-email"
              type="email"
              autoComplete="email"
              placeholder="you@studio.com"
              required
              autoFocus
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </label>
          <label htmlFor="auth-password">
            Password
            <input
              id="auth-password"
              type="password"
              autoComplete={
                mode === "signup" ? "new-password" : "current-password"
              }
              placeholder="At least 8 characters"
              required
              minLength={8}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          </label>

          {error && <div className="AuthPage__error">{error}</div>}

          <button
            type="submit"
            className="AuthPage__submit"
            disabled={isSubmitting}
          >
            {isSubmitting
              ? "Please wait..."
              : mode === "signup"
              ? "Create account"
              : "Log in"}
          </button>
        </form>

        <p className="AuthPage__alternate">
          {mode === "signup" ? "Already have an account?" : "New to DrawMind?"}{" "}
          <button
            type="button"
            onClick={() => changeMode(mode === "signup" ? "login" : "signup")}
          >
            {mode === "signup" ? "Log in" : "Create an account"}
          </button>
        </p>
      </section>
    </main>
  );
};
